/**
 * Výpočet mezd pro akci.
 *
 * Vzorec:
 *   zbytek = cena − pronájem aparatury − doprava aparatury − cestovné muzikantů − stravné
 *   target_pool = zbytek − cílová kapelnická mzda
 *   per_unit = floor(target_pool ÷ Σ podíly, krok)        // základní mzda 100% muzikanta
 *   mzda_i  = floor(per_unit × podíl_i / 100, krok)
 *   kapelnická mzda = zbytek − Σ mzdy
 *
 * Sazby (dodávka, osobní auto) a cílová kapelnická mzda
 * se uloží snapshotem k akci – pozdější změna nastavení historii neovlivní.
 */

export function floorTo(value, step) {
    if (step <= 0) return value;
    return Math.floor(value / step) * step;
}

/**
 * @param {Object} input
 * @param {number} input.price               cena akce
 * @param {number} input.equipmentRent       pronájem aparatury (Kč)
 * @param {number} input.equipmentKm         km cestovní vzdálenosti aparatury
 * @param {number} input.foodAllowance       stravné (Kč)
 * @param {number} input.vanRate             sazba dodávky (Kč/km)
 * @param {number} input.carRate             sazba osobního automobilu (Kč/km)
 * @param {number} input.targetBandWage      cílová kapelnická mzda (default 500)
 * @param {number} input.roundingStep        krok zaokrouhlení (default 100)
 * @param {Array<{id:string, share:number, km:number}>} input.musicians
 * @returns {Object} výsledek
 */
// Cesta tam + zpět – km v DB jsou jednosměrné
const ROUND_TRIP = 2;

// Zaokrouhlení km nahoru na nejbližší pětinásobek (pro cenovou kalkulaci)
const roundKm = (km) => Math.ceil((Number(km) || 0) / 5) * 5;

export function calcEventBudget(input) {
    const {
        price = 0,
        equipmentRent = 0,
        equipmentKm = 0,
        foodAllowance = 0,
        vanRate = 12,
        carRate = 8,
        targetBandWage = 500,
        roundingStep = 100,
        musicians = []
    } = input;

    const equipmentTransport = roundKm(equipmentKm) * ROUND_TRIP * vanRate;
    const musiciansTravel = musicians.reduce((s, m) => s + roundKm(m.km || 0) * ROUND_TRIP * carRate, 0);

    const remainder = price - equipmentRent - equipmentTransport - musiciansTravel - foodAllowance;
    const targetPool = remainder - targetBandWage;

    const shareSum = musicians.reduce((s, m) => s + (m.share || 100) / 100, 0);
    const perUnit = shareSum > 0 ? floorTo(targetPool / shareSum, roundingStep) : 0;

    const wages = musicians.map((m) => {
        const share = (m.share || 100) / 100;
        const wage = floorTo(perUnit * share, roundingStep);
        const travel = roundKm(m.km || 0) * ROUND_TRIP * carRate;
        return {
            id: m.id,
            wage,
            travel,
            payout: wage + travel
        };
    });

    const totalWages = wages.reduce((s, w) => s + w.wage, 0);
    const bandWage = remainder - totalWages;
    const totalCost = totalWages + musiciansTravel + equipmentTransport + equipmentRent + foodAllowance;

    return {
        equipmentTransport,
        musiciansTravel,
        remainder,
        perUnit,
        wages,
        totalWages,
        totalCost,
        bandWage,
        toFund: equipmentRent + bandWage    // co putuje do fondu kapely
    };
}
